import { ReactElement } from 'react'
// next
import NextLink from 'next/link'
// @mui
import {
  Box,
  Link,
  Typography,
  BreadcrumbsProps,
  Breadcrumbs as MUIBreadcrumbs,
} from '@mui/material'
import useLocales from 'hooks/useLocales'

// ----------------------------------------------------------------------

type TLink = {
  href?: string
  name: string
  icon?: ReactElement
  html_id?: string
}

export interface Props extends BreadcrumbsProps {
  links: TLink[]
  activeLast?: boolean
}

export default function Breadcrumbs({
  links,
  activeLast = false,
  ...other
}: Props) {
  const { t } = useLocales()
  const currentLink = t(links[links.length - 1].name)

  const listDefault = links.map((link) => (
    <LinkItem key={t(link.name)} link={link} />
  ))

  const listActiveLast = links.map((link) => (
    <div key={t(link.name)}>
      {t(link.name) !== currentLink ? (
        <LinkItem link={link} />
      ) : (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 260,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            color: 'text.disabled',
            textOverflow: 'ellipsis',
          }}
        >
          {currentLink}
        </Typography>
      )}
    </div>
  ))

  return (
    <MUIBreadcrumbs
      separator={
        <Box
          component="span"
          sx={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: 'text.disabled',
          }}
        />
      }
      {...other}
    >
      {activeLast ? listDefault : listActiveLast}
    </MUIBreadcrumbs>
  )
}

// ----------------------------------------------------------------------

type LinkItemProps = {
  link: TLink
}

function LinkItem({ link }: LinkItemProps) {
  const { href = '', name, icon, html_id } = link
  return (
    <NextLink href={href} passHref>
      <Link
        id={html_id}
        key={name}
        variant="body2"
        sx={{
          lineHeight: 2,
          display: 'flex',
          alignItems: 'center',
          color: 'text.primary',
          '& > div': { display: 'inherit' },
        }}
      >
        {icon && (
          <Box sx={{ mr: 1, '& svg': { width: 20, height: 20 } }}>
            {icon}
          </Box>
        )}
        {name}
      </Link>
    </NextLink>
  )
}
